<script lang="ts">
  import { m } from "#messages";
  import { getLocale } from "#lib/locale.ts";
  import { TONES, hash, toneStep } from "#lib/products/analytics-demo.ts";
  import { readingText } from "#lib/products/demo-reads.ts";
  import { resized } from "#lib/ui/resize.ts";

  let { testId }: { testId: string } = $props();

  const COLS = 64;
  const INSET = 0.1;
  const RADIUS = 0.15;
  const FRAME_MS = 1000 / 15;

  const FLOOR = { side: 0.3, drop: 0.1, tall: 1.4, tilt: 62, depth: 900 } as const;

  const FADE = { top: 0.44, full: 0.72, hold: 0.86 } as const;

  const WEEK = 7;
  const FIRST_COL = Math.round(COLS / 2 - WEEK / 2);
  const READING_AT = 0.8;
  const LIGHT_AT_MS = 700;
  const LIGHT_STEP_MS = 110;
  const LIGHT_MS = 420;

  let stageWidth = $state(0);
  let stageHeight = $state(0);
  let canvas: HTMLCanvasElement | null = null;

  const floorWidth = $derived(stageWidth * (1 + 2 * FLOOR.side));
  const floorHeight = $derived(stageHeight * FLOOR.tall);
  const cell = $derived(floorWidth / COLS);
  const rows = $derived(cell === 0 ? 0 : Math.ceil(floorHeight / cell));
  const top = $derived(floorHeight - rows * cell);

  const project = (u: number, v: number): { x: number; y: number } => {
    const angle = (FLOOR.tilt * Math.PI) / 180;
    const pivot = stageHeight * (1 + FLOOR.drop);
    const rise = v - floorHeight;
    const x = u - FLOOR.side * stageWidth;
    const y = pivot + rise * Math.cos(angle);
    const scale = FLOOR.depth / (FLOOR.depth - rise * Math.sin(angle));
    return { x: stageWidth / 2 + (x - stageWidth / 2) * scale, y: y * scale };
  };

  const readingRow = $derived.by(() => {
    let best = 0;
    let gap = Infinity;
    for (let row = 0; row < rows; row += 1) {
      const miss = Math.abs(project(0, top + (row + 0.5) * cell).y - stageHeight * READING_AT);
      if (miss < gap) {
        gap = miss;
        best = row;
      }
    }
    return best;
  });
  const anchor = $derived(project((FIRST_COL + WEEK / 2) * cell, top + readingRow * cell));

  const seen = (x: number, y: number): boolean => {
    const down = y / stageHeight;
    const across = x / stageWidth;
    return down > FADE.top && down < 1 && across > 0 && across < 1;
  };
  const visible = $derived.by(() => {
    const each = new Uint8Array(rows * COLS);
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < COLS; col += 1) {
        const corners = [
          project(col * cell, top + row * cell),
          project((col + 1) * cell, top + row * cell),
          project(col * cell, top + (row + 1) * cell),
          project((col + 1) * cell, top + (row + 1) * cell),
        ];
        each[row * COLS + col] = corners.some((at) => seen(at.x, at.y)) ? 1 : 0;
      }
    }
    return each;
  });

  const levelAt = (col: number, row: number, t: number): number => {
    const swell = Math.sin(col * 0.16 + t * 0.00055) * Math.cos(row * 0.22 - t * 0.0004);
    const drift = Math.sin((col + row * 1.6) * 0.07 - t * 0.0003);
    const grain = (hash(col, row) - 0.5) * 0.5;
    return (swell + drift + 2) / 4 + grain;
  };

  let colours: { ink: string; series: string } | null = null;

  let litFrom: number | null = null;
  let captioned = $state(false);

  const litAt = (day: number, now: number): number => {
    if (litFrom === null) return 0;
    const age = now - litFrom - day * LIGHT_STEP_MS;
    return Math.min(1, Math.max(0, age / LIGHT_MS));
  };

  let painted = new Float32Array(0);
  let xs = new Float64Array(0);
  let ys = new Float64Array(0);
  let tones = new Uint8Array(0);
  let lights = new Float64Array(0);

  const draw = (now: number): void => {
    if (canvas === null || floorWidth === 0) return;
    const context = canvas.getContext("2d");
    if (context === null) return;
    const ratio = Math.min(devicePixelRatio, 1);
    const pixelsWide = Math.round(floorWidth * ratio);
    const pixelsTall = Math.round(floorHeight * ratio);
    if (colours === null || canvas.width !== pixelsWide || canvas.height !== pixelsTall) {
      canvas.width = pixelsWide;
      canvas.height = pixelsTall;
      const cells = rows * COLS;
      painted = new Float32Array(cells).fill(-1);
      xs = new Float64Array(cells);
      ys = new Float64Array(cells);
      tones = new Uint8Array(cells);
      lights = new Float64Array(cells);
    }
    if (colours === null) {
      const style = getComputedStyle(canvas);
      colours = {
        ink: style.getPropertyValue("--ink-1"),
        series: style.getPropertyValue("--series-1"),
      };
    }
    context.setTransform(ratio, 0, 0, ratio, 0, 0);

    const inset = cell * INSET;
    const size = cell - inset * 2;
    const radius = cell * RADIUS;

    let count = 0;
    for (let row = 0; row < rows; row += 1) {
      const reading = row === readingRow;
      for (let col = 0; col < COLS; col += 1) {
        const index = row * COLS + col;
        if (visible[index] === 0) continue;
        const tone = toneStep(levelAt(col, row, now));
        const day = col - FIRST_COL;
        const lit = reading && day >= 0 && day < WEEK ? litAt(day, now) : 0;
        const state = tone + TONES.length * Math.round(lit * 64);
        if (painted[index] === state) continue;
        painted[index] = state;
        xs[count] = col * cell;
        ys[count] = top + row * cell;
        tones[count] = tone;
        lights[count] = lit * (day === WEEK - 1 ? 1 : 0.82);
        count += 1;
      }
    }
    if (count === 0) return;

    context.globalCompositeOperation = "destination-out";
    context.beginPath();
    for (let at = 0; at < count; at += 1) context.rect(xs[at]!, ys[at]!, cell, cell);
    context.fill();
    context.globalCompositeOperation = "source-over";
    context.fillStyle = colours.ink;
    for (let tone = 0; tone < TONES.length; tone += 1) {
      context.beginPath();
      for (let at = 0; at < count; at += 1) {
        if (tones[at] === tone) {
          context.roundRect(xs[at]! + inset, ys[at]! + inset, size, size, radius);
        }
      }
      context.globalAlpha = TONES[tone]! * 0.45;
      context.fill();
    }
    context.fillStyle = colours.series;
    for (let at = 0; at < count; at += 1) {
      if (lights[at] === 0) continue;
      context.globalAlpha = lights[at]!;
      context.beginPath();
      context.roundRect(xs[at]! + inset, ys[at]! + inset, size, size, radius);
      context.fill();
    }
    context.globalAlpha = 1;
  };

  const run = (node: HTMLElement): (() => void) => {
    const reduced = matchMedia("(prefers-reduced-motion: reduce)");
    let timer = 0;
    let last = 0;
    let onScreen = true;
    const tick = (): void => {
      timer = 0;
      const now = performance.now();
      last = now;
      litFrom ??= now + LIGHT_AT_MS;
      draw(now);
      if (!captioned && litAt(WEEK - 1, now) === 1) captioned = true;
      schedule();
    };
    const schedule = (): void => {
      if (timer !== 0 || !onScreen || document.hidden || reduced.matches) return;
      timer = window.setTimeout(tick, Math.max(0, last + FRAME_MS - performance.now()));
    };
    const still = (): void => {
      litFrom = -Infinity;
      captioned = true;
      draw(8000);
    };
    const observer = new IntersectionObserver((entries) => {
      onScreen = entries.some((entry) => entry.isIntersecting);
      schedule();
    });
    const wake = (): void => {
      if (reduced.matches) still();
      schedule();
    };
    observer.observe(node);
    document.addEventListener("visibilitychange", wake);
    reduced.addEventListener("change", wake);
    wake();
    return () => {
      clearTimeout(timer);
      observer.disconnect();
      document.removeEventListener("visibilitychange", wake);
      reduced.removeEventListener("change", wake);
    };
  };

  $effect(() => {
    if (floorWidth > 0 && matchMedia("(prefers-reduced-motion: reduce)").matches) {
      litFrom = -Infinity;
      draw(8000);
    }
  });
</script>

<div
  class="pointer-events-none absolute inset-0"
  {@attach resized((width, height) => {
    stageWidth = width;
    stageHeight = height;
  })}
>
  <div {@attach run} aria-hidden="true" class="stage" style:perspective="{FLOOR.depth}px">
    <div
      class="floor"
      style:left="{-FLOOR.side * 100}%"
      style:right="{-FLOOR.side * 100}%"
      style:bottom="{-FLOOR.drop * 100}%"
      style:height="{FLOOR.tall * 100}%"
      style:transform="rotateX({FLOOR.tilt}deg)"
    >
      <canvas bind:this={canvas} class="size-full"></canvas>
    </div>
  </div>
  <div
    aria-hidden="true"
    class="fade"
    style:--fade-top="{FADE.top * 100}%"
    style:--fade-full="{FADE.full * 100}%"
    style:--fade-hold="{FADE.hold * 100}%"
  ></div>

  {#if stageWidth > 0}
    <p
      class={["reading text-xs font-medium", captioned && "on"]}
      style:left="{anchor.x}px"
      style:top="{anchor.y}px"
      data-testid={testId}
    >
      <span class="text-(--series-1)">{m.products_pulse_demo_week()}</span>
      <span class="text-ink-1">{readingText(getLocale())}</span>
    </p>
  {/if}
</div>

<style>
  .stage {
    position: absolute;
    inset: 0;
    perspective-origin: 50% 0%;
    overflow: clip;
  }

  .fade {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(
        to right,
        var(--surface-0) 0%,
        transparent 18%,
        transparent 82%,
        var(--surface-0) 100%
      ),
      linear-gradient(
        to bottom,
        var(--surface-0) 0%,
        var(--surface-0) var(--fade-top),
        transparent var(--fade-full),
        transparent var(--fade-hold),
        var(--surface-0) 100%
      );
  }

  .floor {
    position: absolute;
    transform-origin: 50% 100%;
  }

  .reading {
    position: absolute;
    display: flex;
    translate: -50% calc(-100% - 0.6rem);
    gap: 0.5rem;
    white-space: nowrap;
    border-radius: var(--radius-pill);
    background: var(--surface-2);
    padding: 0.25rem 0.65rem;
    opacity: 0;
    transition: opacity 400ms ease-out;
  }

  .reading.on {
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    .reading {
      transition: none;
    }
  }
</style>
